/**
 * ReservationCalendar - Single-Column Timeline with Overlap Detection
 *
 * Google Calendar-style day view. All reservations in one column,
 * overlapping ones laid out side-by-side automatically.
 */

import React, { useState, useMemo } from 'react';
import {
  Box,
  Typography,
  Tooltip,
  Menu,
  MenuItem,
  CircularProgress,
} from '@mui/material';
import {
  ChevronLeft as PrevIcon,
  ChevronRight as NextIcon,
  Today as TodayIcon,
  Add as AddIcon,
  EventSeat as SeatIcon,
  Done as CompleteIcon,
  Cancel as CancelIcon,
  DoNotDisturb as NoShowIcon,
  CheckCircle as ConfirmIcon,
  People as PeopleIcon,
  AccessTime as TimeIcon,
  TableRestaurant as TableIcon,
} from '@mui/icons-material';
import { useReservations } from '../../context/ReservationContext';
import type { Reservation } from '../../types/reservation.types';
import toast from 'react-hot-toast';
import { todayLocalIso, toLocalIsoDate } from '../../utils/dateLocal';

const SLOT_HEIGHT = 48;

const STATUS_STYLES: Record<string, { bg: string; border: string; left: string; text: string }> = {
  pending:   { bg: '#FEF3C7', border: 'var(--status-warn-border)', left: '#E0891E', text: '#8A5411' },
  confirmed: { bg: '#CCFBF1', border: '#5EEAD4', left: '#2F7D5B', text: '#115E59' }, /* basil-todo */
  seated:    { bg: '#D1FAE5', border: '#6EE7B7', left: '#1E8E5A', text: '#10603C' }, /* basil-todo */
  completed: { bg: '#F1F5F9', border: 'var(--border-default)', left: '#64748B', text: '#475569' },
  cancelled: { bg: '#FEE2E2', border: 'var(--status-bad-border)', left: '#D2453C', text: '#9A2E27' },
  no_show:   { bg: '#FEE2E2', border: 'var(--status-bad-border)', left: '#D2453C', text: '#9A2E27' },
};

// --- Overlap detection ---

interface LayoutSlot {
  reservation: Reservation;
  column: number;
  totalColumns: number;
}

function computeLayout(reservations: Reservation[], slotInterval: number): LayoutSlot[] {
  if (reservations.length === 0) return [];

  const toMinutes = (t: string) => {
    const [h, m] = t.split(':').map(Number);
    return h * 60 + m;
  };

  // Sort by start time, then by duration descending (longer first)
  const sorted = [...reservations].sort((a, b) => {
    const diff = toMinutes(a.startTime) - toMinutes(b.startTime);
    if (diff !== 0) return diff;
    return toMinutes(b.endTime) - toMinutes(a.endTime);
  });

  // Build overlap groups using a sweep-line approach
  // Each reservation gets a column assignment
  const columns: { end: number }[] = []; // tracks end time of each column
  const assignments: { reservation: Reservation; column: number; groupId: number }[] = [];

  // Group overlapping reservations
  const groups: { start: number; end: number; members: number[] }[] = [];

  sorted.forEach((res, i) => {
    const start = toMinutes(res.startTime);
    const end = toMinutes(res.endTime);

    // Find the first column where this reservation fits
    let col = -1;
    for (let c = 0; c < columns.length; c++) {
      if (columns[c].end <= start) {
        col = c;
        break;
      }
    }
    if (col === -1) {
      col = columns.length;
      columns.push({ end: 0 });
    }
    columns[col].end = end;

    // Find or create an overlap group
    let groupId = -1;
    for (let g = groups.length - 1; g >= 0; g--) {
      if (start < groups[g].end) {
        groupId = g;
        groups[g].end = Math.max(groups[g].end, end);
        groups[g].members.push(i);
        break;
      }
    }
    if (groupId === -1) {
      groupId = groups.length;
      groups.push({ start, end, members: [i] });
    }

    assignments.push({ reservation: res, column: col, groupId });
  });

  // Compute total columns per group
  const groupMaxCol = new Map<number, number>();
  assignments.forEach(a => {
    const current = groupMaxCol.get(a.groupId) || 0;
    groupMaxCol.set(a.groupId, Math.max(current, a.column + 1));
  });

  return assignments.map(a => ({
    reservation: a.reservation,
    column: a.column,
    totalColumns: groupMaxCol.get(a.groupId) || 1,
  }));
}

// --- Component ---

interface ReservationCalendarProps {
  onCreateReservation: (date?: string, time?: string) => void;
  onViewReservation: (reservation: Reservation) => void;
  searchQuery?: string;
  className?: string;
}

const ReservationCalendar: React.FC<ReservationCalendarProps> = ({
  onCreateReservation,
  onViewReservation,
  searchQuery = '',
  className = '',
}) => {
  const {
    reservations: allReservations,
    selectedDate,
    setSelectedDate,
    settings,
    confirmReservation,
    seatReservation,
    completeReservation,
    cancelReservation,
    markNoShow,
  } = useReservations();

  // Hide cancelled / no-show / completed reservations from the calendar so the
  // grid only shows actionable bookings. Search box re-applies on top.
  const visibleReservations = allReservations.filter(
    (r) => r.status !== 'CANCELLED' && r.status !== 'NO_SHOW' && r.status !== 'COMPLETED'
  );

  // Filter reservations by search query
  const reservations = searchQuery.trim()
    ? visibleReservations.filter(r => {
        const q = searchQuery.toLowerCase();
        return (
          r.customerName?.toLowerCase().includes(q) ||
          r.customerPhone?.includes(q) ||
          r.reservationCode?.toLowerCase().includes(q)
        );
      })
    : visibleReservations;

  const [contextMenu, setContextMenu] = useState<{
    mouseX: number;
    mouseY: number;
    reservation: Reservation;
  } | null>(null);
  const [loading, setLoading] = useState<number | null>(null);

  const slotInterval = settings?.timeSlotIntervalMinutes || 30;
  const openingTime = settings?.openingTime || '10:00';
  const closingTime = settings?.closingTime || '22:00';

  const timeSlots = useMemo(() => {
    const slots: string[] = [];
    const [openH, openM] = openingTime.split(':').map(Number);
    const [closeH, closeM] = closingTime.split(':').map(Number);
    let current = openH * 60 + openM;
    const end = closeH * 60 + closeM;
    while (current <= end) {
      const h = Math.floor(current / 60);
      const m = current % 60;
      slots.push(`${h.toString().padStart(2, '0')}:${m.toString().padStart(2, '0')}`);
      current += slotInterval;
    }
    return slots;
  }, [openingTime, closingTime, slotInterval]);

  // Compute layout with overlap detection
  const layout = useMemo(
    () => computeLayout(reservations, slotInterval),
    [reservations, slotInterval]
  );

  const getBlockStyle = (slot: LayoutSlot) => {
    const [openH, openM] = openingTime.split(':').map(Number);
    const openMin = openH * 60 + openM;
    const [startH, startM] = slot.reservation.startTime.split(':').map(Number);
    const startMin = startH * 60 + startM;
    const [endH, endM] = slot.reservation.endTime.split(':').map(Number);
    const endMin = endH * 60 + endM;

    const top = ((startMin - openMin) / slotInterval) * SLOT_HEIGHT + 2;
    const height = Math.max(((endMin - startMin) / slotInterval) * SLOT_HEIGHT - 4, 40);

    const widthPercent = 100 / slot.totalColumns;
    const leftPercent = slot.column * widthPercent;
    // Small gap between overlapping blocks
    const gap = slot.totalColumns > 1 ? 2 : 0;

    return {
      top: `${top}px`,
      height: `${height}px`,
      left: `calc(${leftPercent}% + ${gap}px)`,
      width: `calc(${widthPercent}% - ${gap * 2}px)`,
    };
  };

  const navigateDate = (days: number) => {
    const current = new Date(selectedDate);
    current.setDate(current.getDate() + days);
    setSelectedDate(toLocalIsoDate(current));
  };

  const goToToday = () => setSelectedDate(todayLocalIso());

  const formatDisplayDate = (dateStr: string) => {
    const d = new Date(dateStr);
    // Short format on mobile via CSS — render both, show one
    return d.toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' });
  };

  const handleContextMenu = (event: React.MouseEvent, reservation: Reservation) => {
    event.preventDefault();
    event.stopPropagation();
    setContextMenu({ mouseX: event.clientX - 2, mouseY: event.clientY - 4, reservation });
  };

  const handleAction = async (action: string, directReservation?: Reservation) => {
    const reservation = directReservation || contextMenu?.reservation;
    if (!reservation) return;
    setLoading(reservation.id);
    setContextMenu(null);
    try {
      switch (action) {
        case 'confirm': await confirmReservation(reservation.id); toast.success('Reservation confirmed'); break;
        case 'seat': await seatReservation(reservation.id); toast.success('Customer seated'); break;
        case 'complete': await completeReservation(reservation.id); toast.success('Reservation completed'); break;
        case 'cancel': await cancelReservation(reservation.id); toast.success('Reservation cancelled'); break;
        case 'noshow': await markNoShow(reservation.id); toast.success('Marked as no-show'); break;
      }
    } catch { toast.error('Action failed'); } finally { setLoading(null); }
  };

  const formatTime12 = (t: string) => {
    const [h, m] = t.split(':').map(Number);
    const ampm = h >= 12 ? 'PM' : 'AM';
    return `${h % 12 || 12}:${m.toString().padStart(2, '0')} ${ampm}`;
  };

  return (
    <>
      <Box sx={{
        bgcolor: 'var(--bg-surface)',
        border: 1,
        borderColor: 'var(--border-subtle)',
        borderRadius: 3,
        overflow: 'hidden',
      }}>
        {/* Header */}
        <Box sx={{
          px: { xs: 1.5, sm: 2.5 },
          py: 1.25,
          borderBottom: 1,
          borderColor: 'var(--border-subtle)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          gap: 1,
        }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, minWidth: 0 }}>
            <Box
              component="button"
              onClick={() => navigateDate(-1)}
              sx={{ width: 36, height: 36, borderRadius: 1.5, border: 1, borderColor: 'var(--border-subtle)', bgcolor: 'var(--bg-sunken)', display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer', flexShrink: 0, '&:hover': { bgcolor: 'var(--bg-sunken)', borderColor: 'var(--border-default)' } }}
            >
              <PrevIcon sx={{ fontSize: 18, color: 'var(--text-inverse)' }} />
            </Box>
            <Box
              component="button"
              onClick={() => navigateDate(1)}
              sx={{ width: 36, height: 36, borderRadius: 1.5, border: 1, borderColor: 'var(--border-subtle)', bgcolor: 'var(--bg-sunken)', display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer', flexShrink: 0, '&:hover': { bgcolor: 'var(--bg-sunken)', borderColor: 'var(--border-default)' } }}
            >
              <NextIcon sx={{ fontSize: 18, color: 'var(--text-inverse)' }} />
            </Box>
            <Typography sx={{ fontWeight: 600, color: 'var(--text-primary)', ml: 0.5, fontSize: { xs: '0.8125rem', sm: '1rem' }, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
              {formatDisplayDate(selectedDate)}
            </Typography>
            {selectedDate !== todayLocalIso() && (
              <Tooltip title="Go to today">
                <Box
                  component="button"
                  onClick={goToToday}
                  sx={{ width: 28, height: 28, borderRadius: 1.5, border: 1, borderColor: 'var(--border-subtle)', bgcolor: 'var(--bg-sunken)', display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer', flexShrink: 0, '&:hover': { bgcolor: 'var(--bg-sunken)', borderColor: 'var(--border-default)' } }}
                >
                  <TodayIcon sx={{ fontSize: 16, color: 'var(--text-secondary)' }} />
                </Box>
              </Tooltip>
            )}
          </Box>
          <Box
            component="button"
            onClick={() => onCreateReservation(selectedDate)}
            sx={{
              background: 'var(--action)',
              color: 'var(--text-inverse)',
              border: 'none',
              px: { xs: 1.25, sm: 2 },
              py: 0.75,
              borderRadius: 2,
              fontWeight: 600,
              fontSize: { xs: '0.75rem', sm: '0.8125rem' },
              display: 'flex',
              alignItems: 'center',
              gap: 0.5,
              cursor: 'pointer',
              flexShrink: 0,
              whiteSpace: 'nowrap',
              '&:hover': { background: 'var(--action-hover)' },
            }}
          >
            <AddIcon sx={{ fontSize: 16 }} />
            <Box component="span" sx={{ display: { xs: 'none', sm: 'inline' } }}>New </Box>
            Reserve
          </Box>
        </Box>

        {/* Calendar body */}
        <Box sx={{ display: 'flex', maxHeight: { xs: 'calc(100dvh - 260px)', sm: 550 }, overflowY: 'auto' }}>
          {/* Time column */}
          <Box sx={{ width: { xs: 50, sm: 70 }, flexShrink: 0, bgcolor: 'var(--bg-sunken)', borderRight: 1, borderColor: 'var(--border-subtle)' }}>
            {timeSlots.map(slot => (
              <Box
                key={slot}
                sx={{
                  height: SLOT_HEIGHT,
                  display: 'flex',
                  alignItems: 'flex-start',
                  justifyContent: 'center',
                  pt: 0.5,
                  fontSize: { xs: '0.625rem', sm: '0.75rem' },
                  color: 'var(--text-tertiary)',
                  borderBottom: 1,
                  borderColor: 'var(--border-subtle)',
                }}
              >
                {slot}
              </Box>
            ))}
          </Box>

          {/* Reservations column */}
          <Box sx={{ flex: 1, position: 'relative', minWidth: 0 }}>
            {/* Background time slots (clickable to create) */}
            {timeSlots.map(slot => (
              <Box
                key={slot}
                onClick={() => onCreateReservation(selectedDate, slot)}
                sx={{
                  height: SLOT_HEIGHT,
                  borderBottom: 1,
                  borderColor: 'var(--border-subtle)',
                  cursor: 'pointer',
                  '&:hover': { bgcolor: 'var(--status-warn-bg)' },
                }}
              />
            ))}

            {/* Reservation blocks */}
            {layout.map(slot => {
              const res = slot.reservation;
              const style = getBlockStyle(slot);
              const heightNum = parseInt(style.height);
              const isCompact = heightNum < 65;
              const isTiny = heightNum < 42;
              const status = res.status.toLowerCase();
              const colors = STATUS_STYLES[status] || STATUS_STYLES.pending;
              const tableName = res.tableGroupName || res.tableName || '';

              return (
                <Box
                  key={res.id}
                  onClick={(e) => { e.stopPropagation(); onViewReservation(res); }}
                  onContextMenu={(e) => handleContextMenu(e, res)}
                  sx={{
                    position: 'absolute',
                    ...style,
                    borderRadius: 2,
                    bgcolor: colors.bg,
                    border: 1,
                    borderColor: colors.border,
                    borderLeft: 4,
                    borderLeftColor: colors.left,
                    px: 1,
                    py: 0.5,
                    cursor: 'pointer',
                    overflow: 'hidden',
                    zIndex: 5,
                    opacity: (status === 'cancelled' || status === 'no_show') ? 0.6 : 1,
                    transition: 'all 0.15s',
                    '&:hover': {
                      transform: 'scale(1.02)',
                      boxShadow: '0 4px 12px rgba(0,0,0,0.12)',
                      zIndex: 10,
                    },
                  }}
                >
                  {isTiny ? (
                    /* Very compact: single line */
                    <Typography noWrap sx={{ fontSize: '0.6875rem', fontWeight: 600, color: colors.text }}>
                      {res.customerName} ({res.partySize}) {tableName}
                    </Typography>
                  ) : isCompact ? (
                    /* Compact: two lines */
                    <>
                      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                        <Typography noWrap sx={{ fontSize: '0.75rem', fontWeight: 600, color: 'var(--text-primary)', lineHeight: 1.2, flex: 1, minWidth: 0 }}>
                          {res.customerName}
                        </Typography>
                        {/* Compact quick actions */}
                        <Box sx={{ display: 'flex', gap: 0.25, ml: 0.5, flexShrink: 0 }} onClick={(e) => e.stopPropagation()}>
                          {res.status === 'PENDING' && (
                            <Tooltip title="Confirm">
                              <Box component="button" onClick={(e) => { e.stopPropagation(); handleAction('confirm', res); }}
                                sx={{ width: 22, height: 22, borderRadius: 1, border: 'none', bgcolor: 'var(--status-info-bg)', display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer', p: 0, '&:hover': { bgcolor: 'var(--status-info-bg)' } }}>
                                <ConfirmIcon sx={{ fontSize: 12, color: 'var(--text-link)' }} />
                              </Box>
                            </Tooltip>
                          )}
                          {(res.status === 'PENDING' || res.status === 'CONFIRMED') && (
                            <Tooltip title="Seat">
                              <Box component="button" onClick={(e) => { e.stopPropagation(); handleAction('seat', res); }}
                                sx={{ width: 22, height: 22, borderRadius: 1, border: 'none', bgcolor: 'var(--status-ok-bg)', display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer', p: 0, '&:hover': { bgcolor: '#A7F3D0' } }}> {/* basil-todo */}
                                <SeatIcon sx={{ fontSize: 12, color: 'var(--status-ok)' }} />
                              </Box>
                            </Tooltip>
                          )}
                        </Box>
                      </Box>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, mt: 0.25 }}>
                        <Typography noWrap sx={{ fontSize: '0.625rem', color: colors.text, display: 'flex', alignItems: 'center', gap: 0.25 }}>
                          <PeopleIcon sx={{ fontSize: 10 }} />{res.partySize}
                        </Typography>
                        {tableName && (
                          <Typography noWrap sx={{ fontSize: '0.625rem', color: 'var(--text-tertiary)', display: 'flex', alignItems: 'center', gap: 0.25 }}>
                            <TableIcon sx={{ fontSize: 10 }} />{tableName}
                          </Typography>
                        )}
                      </Box>
                    </>
                  ) : (
                    /* Full: name, details, time, touch actions */
                    <>
                      <Typography noWrap sx={{ fontSize: '0.8125rem', fontWeight: 600, color: 'var(--text-primary)', lineHeight: 1.2 }}>
                        {res.customerName}
                      </Typography>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 0.5 }}>
                        <Typography sx={{ fontSize: '0.6875rem', color: colors.text, display: 'flex', alignItems: 'center', gap: 0.25 }}>
                          <PeopleIcon sx={{ fontSize: 11 }} />{res.partySize}
                        </Typography>
                        <Typography sx={{ fontSize: '0.6875rem', color: 'var(--text-tertiary)', display: 'flex', alignItems: 'center', gap: 0.25 }}>
                          <TimeIcon sx={{ fontSize: 11 }} />{formatTime12(res.startTime)} - {formatTime12(res.endTime)}
                        </Typography>
                      </Box>
                      {tableName && (
                        <Typography noWrap sx={{ fontSize: '0.625rem', color: 'var(--text-tertiary)', mt: 0.25, display: 'flex', alignItems: 'center', gap: 0.25 }}>
                          <TableIcon sx={{ fontSize: 10 }} />{tableName}
                        </Typography>
                      )}
                      {res.specialRequests && (
                        <Typography noWrap sx={{ fontSize: '0.5625rem', color: 'var(--text-tertiary)', fontStyle: 'italic', mt: 0.25 }}>
                          "{res.specialRequests}"
                        </Typography>
                      )}
                      {/* Touch-friendly quick action buttons */}
                      <Box sx={{ display: 'flex', gap: 0.5, mt: 0.75 }} onClick={(e) => e.stopPropagation()}>
                        {res.status === 'PENDING' && (
                          <Box component="button" onClick={(e) => { e.stopPropagation(); handleAction('confirm', res); }}
                            sx={{ height: 24, px: 0.75, borderRadius: 1.5, border: 'none', bgcolor: 'var(--status-info-bg)', display: 'flex', alignItems: 'center', gap: 0.25, cursor: 'pointer', fontSize: '0.625rem', fontWeight: 600, color: 'var(--text-link)', '&:hover': { bgcolor: 'var(--status-info-bg)' }, '&:active': { bgcolor: '#93C5FD' } }}> {/* basil-todo */}
                            {loading === res.id ? <CircularProgress size={10} /> : <><ConfirmIcon sx={{ fontSize: 12 }} />Confirm</>}
                          </Box>
                        )}
                        {(res.status === 'PENDING' || res.status === 'CONFIRMED') && (
                          <Box component="button" onClick={(e) => { e.stopPropagation(); handleAction('seat', res); }}
                            sx={{ height: 24, px: 0.75, borderRadius: 1.5, border: 'none', bgcolor: 'var(--status-ok-bg)', display: 'flex', alignItems: 'center', gap: 0.25, cursor: 'pointer', fontSize: '0.625rem', fontWeight: 600, color: 'var(--status-ok)', '&:hover': { bgcolor: '#A7F3D0' }, '&:active': { bgcolor: '#6EE7B7' } }}> {/* basil-todo */}
                            {loading === res.id ? <CircularProgress size={10} /> : <><SeatIcon sx={{ fontSize: 12 }} />Seat</>}
                          </Box>
                        )}
                        {res.status === 'SEATED' && (
                          <Box component="button" onClick={(e) => { e.stopPropagation(); handleAction('complete', res); }}
                            sx={{ height: 24, px: 0.75, borderRadius: 1.5, border: 'none', bgcolor: 'var(--bg-sunken)', display: 'flex', alignItems: 'center', gap: 0.25, cursor: 'pointer', fontSize: '0.625rem', fontWeight: 600, color: 'var(--text-secondary)', '&:hover': { bgcolor: 'var(--bg-sunken)' } }}>
                            <CompleteIcon sx={{ fontSize: 12 }} />Done
                          </Box>
                        )}
                        {(res.status === 'PENDING' || res.status === 'CONFIRMED') && (
                          <Box component="button" onClick={(e) => { e.stopPropagation(); handleAction('cancel', res); }}
                            sx={{ height: 24, px: 0.75, borderRadius: 1.5, border: 'none', bgcolor: 'var(--status-bad-bg)', display: 'flex', alignItems: 'center', gap: 0.25, cursor: 'pointer', fontSize: '0.625rem', fontWeight: 600, color: 'var(--status-bad)', '&:hover': { bgcolor: '#FECACA' } }}> {/* basil-todo */}
                            <CancelIcon sx={{ fontSize: 12 }} />Cancel
                          </Box>
                        )}
                      </Box>
                    </>
                  )}
                </Box>
              );
            })}

            {/* Empty state */}
            {reservations.length === 0 && (
              <Box sx={{
                position: 'absolute',
                top: '50%',
                left: '50%',
                transform: 'translate(-50%, -50%)',
                textAlign: 'center',
                color: 'var(--text-tertiary)',
              }}>
                <Typography variant="body1" sx={{ fontWeight: 500, mb: 0.5 }}>
                  No reservations
                </Typography>
                <Typography variant="body2" sx={{ mb: 1.5 }}>
                  Click a time slot to create one
                </Typography>
              </Box>
            )}
          </Box>
        </Box>

        {/* Legend */}
        {reservations.length > 0 && (
          <Box sx={{ px: 2, py: 1, borderTop: 1, borderColor: 'var(--border-subtle)', display: 'flex', gap: 2, flexWrap: 'wrap' }}>
            {[
              { label: 'Pending', color: 'var(--status-warn)' },
              { label: 'Confirmed', color: 'var(--text-link)' },
              { label: 'Seated', color: 'var(--status-ok)' },
            ].map(item => (
              <Box key={item.label} sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                <Box sx={{ width: 10, height: 10, borderRadius: '50%', bgcolor: item.color }} />
                <Typography variant="caption" sx={{ color: 'var(--text-tertiary)' }}>{item.label}</Typography>
              </Box>
            ))}
          </Box>
        )}
      </Box>

      {/* Context Menu */}
      <Menu
        open={contextMenu !== null}
        onClose={() => setContextMenu(null)}
        anchorReference="anchorPosition"
        anchorPosition={contextMenu ? { top: contextMenu.mouseY, left: contextMenu.mouseX } : undefined}
        PaperProps={{ sx: { borderRadius: 2, boxShadow: '0 4px 12px rgba(0,0,0,0.15)' } }}
      >
        {contextMenu?.reservation.status === 'PENDING' && (
          <MenuItem onClick={() => handleAction('confirm')}>
            <ConfirmIcon fontSize="small" sx={{ mr: 1.5, color: 'var(--text-link)' }} />Confirm
          </MenuItem>
        )}
        {(contextMenu?.reservation.status === 'PENDING' || contextMenu?.reservation.status === 'CONFIRMED') && (
          <MenuItem onClick={() => handleAction('seat')}>
            <SeatIcon fontSize="small" sx={{ mr: 1.5, color: 'var(--status-ok)' }} />Seat Customer
          </MenuItem>
        )}
        {contextMenu?.reservation.status === 'SEATED' && (
          <MenuItem onClick={() => handleAction('complete')}>
            <CompleteIcon fontSize="small" sx={{ mr: 1.5, color: 'var(--text-tertiary)' }} />Complete
          </MenuItem>
        )}
        {(contextMenu?.reservation.status === 'PENDING' || contextMenu?.reservation.status === 'CONFIRMED') && [
          <MenuItem key="cancel" onClick={() => handleAction('cancel')}>
            <CancelIcon fontSize="small" sx={{ mr: 1.5, color: 'var(--status-bad)' }} />Cancel
          </MenuItem>,
          <MenuItem key="noshow" onClick={() => handleAction('noshow')}>
            <NoShowIcon fontSize="small" sx={{ mr: 1.5, color: 'var(--status-bad)' }} />Mark No-Show
          </MenuItem>,
        ]}
      </Menu>
    </>
  );
};

export default ReservationCalendar;
